/* eslint-disable */
// Motor de turno (runTurn) + helpers PUROS — COPIA de backend/controllers/careerController.js (ESM).
import { generateCalendar, roundsForCount } from './calendar';
import { simulateMatch, simulateExtraTime, simulatePenaltyShootout, rivalTacticas } from './matchEngine';
import { applyPromotionRelegation, startNewSeason, sortStandings, selectEuropeanPools, buildEuropeanCompetition, selectConmebolPools, buildConmebolCompetition, startConmebolKnockouts, computeEuroSchedule, startKnockout, pairsFromVivos, advanceKnockout, EURO_ROUNDS } from './seasonEngine';
import { generateTransferOffers, buildRenewalOffer, clubWantsRenewal, computeMarketValue } from './transferEngine';
import { worldStandings, worldStandingsPartial } from './worldSim';
import * as PM from './playerModel';
import { seleccionDelJugador, isCalledUp } from './selecciones';
import {
    iniciarCicloClasif, aplicarPartidoJugador, refrescarStandingsGrupoJugador, procesarFinDeAño, determinarClasificacion,
    seleccionScheduleEnSeason, construirPlayoffJugador, resolverRivalFinal, iniciarMundialPlayer, iniciarMundialEspectador, avanzarMundialEspectador,
    simularPartidosBackgroundGrupo, simularOtrosGruposHastaFecha, simularGruposBackgroundMundial,
    iniciarKO, cruceKOJugador, avanzarRondaKO, avanzarMundialEliminado,
    aplicarResultadoStandings, ordenarStandings,
} from './seleccionEngine';
import { SELECCIONES_BY_PAIS } from './selecciones';

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

// Arma el objeto `player` para simulateMatch incluyendo la pizarra táctica del usuario y el
// esquema (determinista) del rival, para que el motor cruce los planteos. `key` debe ser estable
// por partido para que el scouting que ve el usuario coincida con lo que juega el rival.
function buildPlayer(career, isHome, rivalClub, key) {
    return {
        rating: PM.efectiveRating(career.rating, career.moral),
        posicion: career.posicion,
        mentalidad: career.mentalidad,
        tacticas: career.tacticas,
        rivalTacticas: rivalClub ? rivalTacticas(rivalClub, key) : null,
        isHome,
    };
}

function prunePlayedFixtures(career) {
    for (const league of career.leagues || []) {
        if (!league.calendario) continue;
        league.calendario = league.calendario.filter(j => j.partidos.some(p => !p.jugado));
    }
    // Las copas también: una vez jugada una jornada de grupos ya no se vuelve a leer, y al
    // pasar a eliminatorias el calendario de grupos queda muerto. Podarlo achica el documento
    // (menos datos que serializar/enviar a Atlas en cada avance → avances más rápidos).
    for (const comp of [career.champions_league, career.europa_league, career.libertadores, career.sudamericana]) {
        if (!comp || !comp.calendario) continue;
        comp.calendario = comp.fase === 'grupos'
            ? comp.calendario.filter(j => j.partidos.some(p => !p.jugado))
            : [];
    }
}

function esLigaDelJugador(league, career) {
    return league.pais === career.club_actual?.pais && league.division === career.club_actual?.division;
}

// Cantidad de jornadas ya disputadas en `league` cuando el jugador se incorpora a mitad de
// temporada. Escala la jornada actual del jugador a la cantidad de fechas de la liga nueva.
// Debe coincidir EXACTAMENTE con la versión del backend (controllers/careerController.js) para
// que la posición de la oferta (calculada acá) coincida con la tabla con la que arranca el club
// al fichar (calculada en el backend al aceptar).
function jornadasJugadasAlIncorporarse(career, league) {
    const playerLeague = career.leagues.find(l => esLigaDelJugador(l, career));
    const totalJugador = roundsForCount((playerLeague || league).clubs.length);
    const newTotal = roundsForCount(league.clubs.length);
    if (!totalJugador) return 0;
    return Math.max(0, Math.min(newTotal - 1, Math.floor((career.jornada_actual * newTotal) / totalJugador)));
}

// Tabla determinística de mitad de temporada de `league` al incorporarse el jugador. La MISMA
// simulación que aplicará el backend al aceptar la oferta → la posición prometida se respeta.
function partialLeagueAlIncorporarse(career, league) {
    const jugadas = jornadasJugadasAlIncorporarse(career, league);
    const { tabla, calendario } = worldStandingsPartial(
        league.clubs, career.seed || 0, career.temporada, league.nombre, jugadas
    );
    return { tabla, calendario, jornadasJugadas: jugadas };
}

// Simula una temporada COMPLETA de una liga en memoria y deja la tabla final en clubs[], SIN
// guardar el calendario. Las ligas que no son la del jugador no necesitan fixtures persistidos
// (el jugador nunca las juega ni ve sus partidos), y guardarlos era el 73% del documento.
function simulateLeagueToFinal(league) {
    for (const c of league.clubs) { c.pj = 0; c.pg = 0; c.pe = 0; c.pp = 0; c.gf = 0; c.gc = 0; c.pts = 0; }
    const byId = new Map(league.clubs.map(c => [c.tm_id, c]));
    for (const j of generateCalendar(league.clubs.map(c => c.tm_id))) {
        for (const p of j.partidos) {
            if (!p.home_tm_id || !p.away_tm_id) continue;
            const h = byId.get(p.home_tm_id), a = byId.get(p.away_tm_id);
            if (!h || !a) continue;
            const { gh, ga } = simulateMatch(h, a, null);
            updateTabla(h, a, gh, ga);
        }
    }
    league.calendario = [];
}

// Prepara los calendarios de una temporada: SOLO la liga del jugador lleva calendario real (se
// juega jornada a jornada); las demás se simulan a tabla final al instante. Se llama al elegir
// club y al arrancar cada temporada nueva.
function setupSeasonCalendars(career) {
    for (const league of career.leagues) {
        if (esLigaDelJugador(league, career)) {
            league.calendario = generateCalendar(league.clubs.map(c => c.tm_id));
        } else if (career.seed) {
            // Mundo determinístico: las ligas ajenas NO se simulan ni se guardan. Su tabla se
            // recalcula on-demand con worldSim a partir de (seed, temporada, ligaId). Solo dejamos
            // la identidad de los clubes en 0 y sin calendario.
            for (const c of league.clubs) { c.pj = 0; c.pg = 0; c.pe = 0; c.pp = 0; c.gf = 0; c.gc = 0; c.pts = 0; }
            league.calendario = [];
        } else {
            simulateLeagueToFinal(league);
        }
    }
}

// Opciones para la simulación determinística del mundo (worldSim) en las funciones de
// seasonEngine. Sin seed (partidas legacy) devuelve un objeto inerte → comportamiento de antes.
function worldOpts(career) {
    return {
        seed: career.seed || 0,
        temporada: career.temporada,
        esLigaJugador: (league) => esLigaDelJugador(league, career),
    };
}

// Migración/saneamiento: finaliza al instante cualquier liga que NO sea la del jugador y que
// todavía tenga calendario guardado (partidas viejas guardaban los calendarios de las 34 ligas).
// Simula lo que falte de su temporada y descarta el calendario → el documento se achica.
function finalizeOtherLeagues(career) {
    for (const league of career.leagues) {
        if (esLigaDelJugador(league, career)) continue;
        if (!league.calendario || league.calendario.length === 0) continue;
        // Mundo determinístico: una liga que el jugador dejó no se simula (su tabla se recalcula
        // con worldSim igual). Solo descartamos su calendario y reseteamos stats.
        if (career.seed) {
            for (const c of league.clubs) { c.pj = 0; c.pg = 0; c.pe = 0; c.pp = 0; c.gf = 0; c.gc = 0; c.pts = 0; }
            league.calendario = [];
            continue;
        }
        const byId = new Map(league.clubs.map(c => [c.tm_id, c]));
        for (const j of league.calendario) {
            for (const p of j.partidos) {
                if (p.jugado || !p.home_tm_id || !p.away_tm_id) continue;
                const h = byId.get(p.home_tm_id), a = byId.get(p.away_tm_id);
                if (!h || !a) continue;
                const { gh, ga } = simulateMatch(h, a, null);
                updateTabla(h, a, gh, ga);
            }
        }
        league.calendario = [];
    }
}

// Mes del calendario europeo según el avance de la temporada (agosto a mayo).
const MESES_TEMPORADA = [8, 9, 10, 11, 12, 1, 2, 3, 4, 5];

function computeMes(jornada, totalJornadas) {
    if (!totalJornadas) return 8;
    const idx = Math.min(MESES_TEMPORADA.length - 1, Math.floor(((jornada - 1) / totalJornadas) * MESES_TEMPORADA.length));
    return MESES_TEMPORADA[idx];
}

// Actualiza pj/pg/pe/pp/gf/gc/pts de ambos clubes según el resultado.
function updateTabla(homeClub, awayClub, gh, ga) {
    homeClub.pj += 1;
    awayClub.pj += 1;
    homeClub.gf += gh;
    homeClub.gc += ga;
    awayClub.gf += ga;
    awayClub.gc += gh;

    if (gh > ga) {
        homeClub.pg += 1; homeClub.pts += 3;
        awayClub.pp += 1;
    } else if (gh < ga) {
        awayClub.pg += 1; awayClub.pts += 3;
        homeClub.pp += 1;
    } else {
        homeClub.pe += 1; homeClub.pts += 1;
        awayClub.pe += 1; awayClub.pts += 1;
    }
}

// ¿El jugador tiene un partido por jugar en este slot europeo (grupo o eliminatoria)?
function playerEnEuroSlot(comp, myTmId, euroSlot) {
    if (!comp || !comp.clubs || !comp.clubs.some(c => c.tm_id === myTmId)) return false;
    if (euroSlot.tipo === 'grupo') {
        if (comp.fase !== 'grupos') return false;
        const jornada = (comp.calendario || []).find(j => j.jornada === euroSlot.ronda);
        if (!jornada) return false;
        return jornada.partidos.some(p => !p.jugado && (p.home_tm_id === myTmId || p.away_tm_id === myTmId));
    }
    // Eliminatoria: el jugador juega si está vivo Y el slot de turno corresponde a la ronda
    // actual de ESTA copa. Libertadores arranca directo en octavos (se saltea los 16avos), así
    // que su ronda no siempre coincide con el slot KO en curso: sin este chequeo, se marcaba
    // erróneamente que el jugador tenía partido en slots donde su copa no juega → quedaba
    // trabado mostrando "es eliminatoria" sin poder disputarla nunca.
    if (comp.fase !== 'knockout') return false;
    if (euroSlot.ronda !== comp.ko_ronda) return false;
    return (comp.ko_vivos || []).includes(myTmId);
}

// Decide si el PRÓXIMO "avanzar" será un turno de partido europeo del jugador (true) o una
// jornada de liga (false). Sirve para anticiparlo en la UI y para pausar el modo automático.
function computeProximoEsEuro(career, totalJornadas) {
    const nextJornada = (career.jornada_actual || 0) + 1;
    if (nextJornada > totalJornadas) return false;
    const euroSlot = computeEuroSchedule(totalJornadas).get(nextJornada);
    if (!euroSlot) return false;
    if (career.euro_turno_jornada === nextJornada) return false; // el turno europeo ya se jugó
    const myTmId = career.club_actual?.tm_id;
    return playerEnEuroSlot(career.champions_league, myTmId, euroSlot)
        || playerEnEuroSlot(career.europa_league, myTmId, euroSlot)
        || playerEnEuroSlot(career.libertadores, myTmId, euroSlot)
        || playerEnEuroSlot(career.sudamericana, myTmId, euroSlot);
}

// Devuelve un preview del PRÓXIMO partido de selección del jugador (clasif, repechaje o Mundial)
// o null si el próximo turno no es de selección. Lo usa el frontend para: frenar el automático,
// mostrar el partido (rival + instancia) en vez del próximo de liga, y pintar la pantalla distinto.
function computeSeleccionNext(career, totalJornadas) {
    const sel = career.seleccion;
    if (!sel) return null;
    const nextJornada = (career.jornada_actual || 0) + 1;
    const selRef = seleccionDelJugador(career);
    if (!selRef) return null;
    const convocado = isCalledUp(career, selRef);

    // Mundial del jugador EN CURSO (se juega a mitad del año 4): próximo rival.
    const mAct = sel.mundial;
    if (mAct && (mAct.fase === 'grupos' || mAct.fase === 'ko')) {
        if (mAct.fase === 'grupos') {
            for (let f = 0; f < 3; f++) {
                for (const p of (mAct.fechas[f] || [])) {
                    if (p.jugado) continue;
                    if (p.home?.pais === sel.pais || p.away?.pais === sel.pais) {
                        const esLocal = p.home.pais === sel.pais;
                        const riv = esLocal ? p.away : p.home;
                        return { competicion: 'mundial-grupo', instancia: `Mundial · Fase de grupos · Fecha ${f + 1}`, rival_nombre: riv.nombre, rival_pais: riv.pais, local: esLocal, convocado };
                    }
                }
            }
            // Ya jugó las 3 fechas de grupo: no hay "próximo rival" todavía (se resuelve KO/eliminación
            // en el siguiente avance). No devolvemos un rival fantasma "—".
            return null;
        }
        const cruce = cruceKOJugador(mAct, sel.pais);
        if (cruce) {
            const riv = SELECCIONES_BY_PAIS[cruce.rival_pais];
            const RL = { '16avos': '16avos', octavos: 'Octavos', cuartos: 'Cuartos', semis: 'Semifinal', final: 'Final' };
            return { competicion: 'mundial-ko', instancia: `Mundial · ${RL[mAct.ko_ronda] || mAct.ko_ronda}`, rival_nombre: riv?.nombre || '—', rival_pais: cruce.rival_pais, local: true, convocado };
        }
        return null;
    }

    // Pre-repechaje CONCACAF (año 1).
    if (sel.año_ciclo === 1 && sel.fase === 'preplayoff' && sel.preplayoff && sel.preplayoff.fase === 'jugando' && sel.preplayoff_turno_jornada !== nextJornada) {
        const pp = sel.preplayoff;
        const jornadaTie = 2 + pp.idx * 4;
        if (nextJornada === jornadaTie && pp.idx < pp.ties.length) {
            const tie = pp.ties[pp.idx];
            return { competicion: 'preplayoff', instancia: `CONCACAF · ${tie.ronda}`, rival_nombre: tie.rival_nombre, rival_pais: tie.rival_pais, local: true, convocado };
        }
    }

    // Clasificatorias (año 1-3).
    if (sel.año_ciclo >= 1 && sel.año_ciclo <= 3 && sel.fase === 'clasif' && sel.turno_jornada !== nextJornada) {
        const schedule = seleccionScheduleEnSeason(sel, sel.año_ciclo, totalJornadas);
        const idx = schedule.get(nextJornada);
        if (idx != null) {
            const p = sel.calendarioJugador[idx];
            if (p && !p.jugado) {
                return { competicion: 'clasificatoria', instancia: `Eliminatorias · Año ${sel.año_ciclo}/3`, rival_nombre: p.rival.nombre, rival_pais: p.rival.pais, local: p.local, convocado };
            }
        }
    }

    // Repechaje (año 4, antes del Mundial).
    if (sel.año_ciclo === 4 && sel.fase === 'playoff' && sel.playoff && sel.playoff.fase === 'jugando' && sel.playoff_turno_jornada !== nextJornada) {
        const po = sel.playoff;
        const jornadaTie = 3 + po.idx * 5;
        if (nextJornada === jornadaTie && po.idx < po.ties.length) {
            const tie = po.ties[po.idx];
            const legTxt = tie.leg === 'ida' ? ' (ida)' : tie.leg === 'vuelta' ? ' (vuelta)' : '';
            return { competicion: 'playoff', instancia: `Repechaje · ${tie.ronda}${legTxt}`, rival_nombre: tie.rival_nombre, rival_pais: tie.rival_pais, local: tie.leg !== 'vuelta', convocado };
        }
    }
    return null;
}

// ── MUNDIAL: simulación turno-a-turno (Stage 4) ─────────────────────────────
// Cuando el jugador clasificó al Mundial (fin del año 4), arrancamos un mini-torneo de turnos:
// 3 partidos de fase de grupos, luego KO (16avos/octavos/cuartos/semis/final). Cada turno simula
// UN partido del jugador (con modal vivo) y devuelve `partidoSeleccion`.

// Empuja un partido de selección a "últimos resultados" (solo si jugaste, es decir convocado).
function pushHistorialSeleccion(career, ps) {
    if (!ps || ps.no_convocado || ps.eliminado_mundial) return;
    career.historial_partidos.push({
        temporada: career.temporada,
        jornada: career.jornada_actual,
        rival_nombre: ps.rival_nombre,
        rival_pais: ps.rival_pais,
        local: !!ps.local,
        gf: ps.gf, gc: ps.gc,
        minutos_jugados: ps.minutos_jugados || 0,
        goles_jugador: ps.goles_jugador || 0,
        asistencias_jugador: ps.asistencias_jugador || 0,
        amarilla: !!ps.amarilla,
        roja: !!ps.roja,
        competicion: 'seleccion',
    });
    if (career.historial_partidos.length > 10) career.historial_partidos = career.historial_partidos.slice(-10);
}

function jugarSiguientePartidoMundial(career) {
    const m = career.seleccion.mundial;
    const paisJ = career.seleccion.pais;
    const selJ = SELECCIONES_BY_PAIS[paisJ];

    // Helper para construir el resultado a devolver y registrar stats.
    const armarYRetornar = (partidoSeleccion) => {
        career.proximo_turno_es_euro = false;
        career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
        pushHistorialSeleccion(career, partidoSeleccion);
        return { career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion, suspendido: false, esTurnoEuro: false, esTurnoSeleccion: true, temporadaFinalizada: false };
    };

    // ── FASE GRUPOS ──
    if (m.fase === 'grupos') {
        // Buscar el próximo partido del jugador en las fechas 0..2.
        let nextP = null, nextF = 0;
        for (let f = 0; f < 3 && !nextP; f++) {
            for (const p of m.fechas[f] || []) {
                if (p.jugado) continue;
                if (p.home?.pais === paisJ || p.away?.pais === paisJ) { nextP = p; nextF = f; break; }
            }
        }
        if (!nextP) {
            // Ya jugó las 3 fechas: simular el resto del grupo del jugador + decidir KO o eliminación.
            simularPartidosBackgroundGrupo(m, paisJ, career.seed || 1, career.seleccion.ciclo, 2);
            // Otros grupos también: para que la tabla del torneo esté lista para KO.
            simularGruposBackgroundMundial(m, paisJ, career.seed || 1, career.seleccion.ciclo);
            const grupoJ = m.grupos[m.grupoJugadorIdx];
            const tabla = ordenarStandings(grupoJ.standings);
            const posJ = tabla.findIndex(t => t.pais === paisJ) + 1;
            const enKO = posJ <= 2 || esMejorTerceroMundial(m, paisJ);
            if (enKO) {
                // Clasificó a la fase final: armamos el cuadro y devolvemos un turno de PRESENTACIÓN
                // (sin jugar) para que el rival de 16avos aparezca en pantalla ANTES de jugarlo.
                iniciarKO(m, career.seed || 1, career.seleccion.ciclo);
                const cruce = cruceKOJugador(m, paisJ);
                const rivalSel = cruce ? SELECCIONES_BY_PAIS[cruce.rival_pais] : null;
                career.proximo_turno_es_euro = false;
                career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
                return {
                    career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion: null,
                    esMundialKOIntro: true, koRonda: m.ko_ronda, rivalNombre: rivalSel?.nombre || '—',
                    rivalPais: cruce?.rival_pais || null, posicionGrupo: posJ, ciclo: career.seleccion.ciclo,
                    suspendido: false, esTurnoEuro: false, esTurnoSeleccion: false, temporadaFinalizada: false,
                };
            } else {
                // Eliminado en grupos: dejamos que el torneo siga (espectador) para ver cómo termina.
                m.llego_a = 'grupos';
                iniciarKO(m, career.seed || 1, career.seleccion.ciclo);
                m.fase = 'eliminado_espectador';
                return armarYRetornar({
                    competicion: 'mundial-grupo',
                    eliminado_mundial: true,
                    posicion_grupo: posJ,
                    local: false, rival_nombre: '—', gf: 0, gc: 0,
                    minutos_jugados: 0, goles_jugador: 0, asistencias_jugador: 0,
                    amarilla: false, roja: false, no_convocado: false,
                    fecha: 3, ciclo: career.seleccion.ciclo,
                });
            }
        }

        // Simular partido del jugador en grupos.
        // Background: los OTROS partidos de la misma fecha del grupo del jugador.
        simularPartidosBackgroundGrupo(m, paisJ, career.seed || 1, career.seleccion.ciclo, nextF);
        // Los demás 11 grupos también avanzan fecha a fecha, para que la tabla de "mejores
        // terceros" se mantenga al día partido a partido en vez de quedar en cero hasta el final.
        simularOtrosGruposHastaFecha(m, career.seed || 1, career.seleccion.ciclo, nextF);
        const convocado = (career.rating || 0) > (selJ ? umbralCallupSel(selJ) : 0);
        const homeClub = { tm_id: nextP.home.pais, nombre: nextP.home.nombre, estrellas: nextP.home.estrellas };
        const awayClub = { tm_id: nextP.away.pais, nombre: nextP.away.nombre, estrellas: nextP.away.estrellas };
        const esLocal = nextP.home.pais === paisJ;
        let result;
        if (convocado) {
            const player = {
                rating: PM.efectiveRating(career.rating, career.moral),
                posicion: career.posicion, mentalidad: career.mentalidad, tacticas: career.tacticas,
                rivalTacticas: rivalTacticas(esLocal ? awayClub : homeClub, `mundialG-${career.seleccion.ciclo}-${nextF}-${(esLocal ? awayClub : homeClub).tm_id}`),
                isHome: esLocal,
            };
            result = simulateMatch(homeClub, awayClub, player);
        } else {
            result = simulateMatch(homeClub, awayClub, null);
        }
        nextP.gh = result.gh; nextP.ga = result.ga; nextP.jugado = true;
        const grupoJ = m.grupos[m.grupoJugadorIdx];
        aplicarResultadoStandings(grupoJ.standings, nextP.home, nextP.away, result.gh, result.ga);
        m.partidosJugadorJugados += 1;
        if (convocado) {
            m.partidosJugadorConvocado += 1;
            career.seleccion.stats_ciclo.partidos += 1;
            career.seleccion.stats_ciclo.goles += result.golesJugador;
            career.seleccion.stats_ciclo.asistencias += result.asistenciasJugador;
            career.seleccion_carrera.partidos += 1;
            career.seleccion_carrera.goles += result.golesJugador;
            career.seleccion_carrera.asistencias += result.asistenciasJugador;
            if (result.amarilla) career.seleccion_carrera.amarillas += 1;
            if (result.roja) career.seleccion_carrera.rojas += 1;
        }
        m.partidosJugador.push({ tipo: 'grupo', fecha: nextF + 1, rival_pais: (esLocal ? nextP.away.pais : nextP.home.pais), gf: esLocal ? result.gh : result.ga, gc: esLocal ? result.ga : result.gh, goles: convocado ? result.golesJugador : 0, asistencias: convocado ? result.asistenciasJugador : 0 });
        return armarYRetornar({
            competicion: 'mundial-grupo',
            rival_nombre: esLocal ? nextP.away.nombre : nextP.home.nombre,
            rival_pais: esLocal ? nextP.away.pais : nextP.home.pais,
            local: esLocal,
            gf: esLocal ? result.gh : result.ga,
            gc: esLocal ? result.ga : result.gh,
            minutos_jugados: convocado ? result.minutosJugador : 0,
            goles_jugador: convocado ? result.golesJugador : 0,
            asistencias_jugador: convocado ? result.asistenciasJugador : 0,
            amarilla: convocado ? result.amarilla : false,
            roja: convocado ? result.roja : false,
            no_convocado: !convocado,
            fecha: nextF + 1,
            pais_jugador: paisJ,
            ciclo: career.seleccion.ciclo,
        });
    }

    // ── FASE KO ──
    if (m.fase === 'ko') {
        const cruce = cruceKOJugador(m, paisJ);
        if (!cruce) {
            // Jugador ya no está en ko_vivos (perdió en una ronda anterior). Cierre.
            m.fase = 'finalizado';
            return armarYRetornar({ competicion: 'mundial-ko', eliminado_mundial: true, local: false, rival_nombre: '—', gf: 0, gc: 0, minutos_jugados: 0, goles_jugador: 0, asistencias_jugador: 0, amarilla: false, roja: false, no_convocado: false, ronda: m.ko_ronda, ciclo: career.seleccion.ciclo });
        }
        const rivalSel = SELECCIONES_BY_PAIS[cruce.rival_pais];
        const convocado = (career.rating || 0) > (selJ ? umbralCallupSel(selJ) : 0);
        const homeClub = { tm_id: paisJ, nombre: selJ.nombre, estrellas: selJ.estrellas };
        const awayClub = { tm_id: cruce.rival_pais, nombre: rivalSel.nombre, estrellas: rivalSel.estrellas };
        const player = convocado ? {
            rating: PM.efectiveRating(career.rating, career.moral),
            posicion: career.posicion, mentalidad: career.mentalidad, tacticas: career.tacticas,
            rivalTacticas: rivalTacticas(awayClub, `mundialK-${career.seleccion.ciclo}-${m.ko_ronda}-${cruce.rival_pais}`),
            isHome: true, // por convención el jugador es "home" en su partido KO (no afecta a fondo)
        } : null;
        const result = simulateMatch(homeClub, awayClub, player);
        // Sin empates en KO: si sigue igualado a los 90', se juega la prórroga (2x15) y recién
        // si persiste el empate se define por penales (tanda real, no una moneda al aire).
        let gh = result.gh, ga = result.ga;
        let golesJugador = result.golesJugador, asistenciasJugador = result.asistenciasJugador;
        let amarilla = result.amarilla, roja = result.roja, minutosJugador = result.minutosJugador;
        let prorroga = false, penales = false, penalShootout = null;
        if (gh === ga) {
            prorroga = true;
            const et = simulateExtraTime(homeClub, awayClub, player);
            gh += et.gh; ga += et.ga;
            golesJugador += et.golesJugador; asistenciasJugador += et.asistenciasJugador;
            amarilla = amarilla || et.amarilla; roja = roja || et.roja;
            minutosJugador += et.minutosJugador;
            if (gh === ga) {
                penales = true;
                penalShootout = simulatePenaltyShootout(selJ.estrellas, rivalSel.estrellas);
                if (penalShootout.ganador === 'a') gh += 1; else ga += 1;
            }
        }
        const ganador_pais = gh > ga ? paisJ : cruce.rival_pais;
        m.ko_resultados.push({ ronda: m.ko_ronda, pais_rival: cruce.rival_pais, gf: gh, gc: ga, ganador_pais, penales, prorroga, penalShootout });
        if (convocado) {
            m.partidosJugadorConvocado += 1;
            career.seleccion.stats_ciclo.partidos += 1;
            career.seleccion.stats_ciclo.goles += golesJugador;
            career.seleccion.stats_ciclo.asistencias += asistenciasJugador;
            career.seleccion_carrera.partidos += 1;
            career.seleccion_carrera.goles += golesJugador;
            career.seleccion_carrera.asistencias += asistenciasJugador;
            if (amarilla) career.seleccion_carrera.amarillas += 1;
            if (roja) career.seleccion_carrera.rojas += 1;
        }
        m.partidosJugador.push({ tipo: 'ko', ronda: m.ko_ronda, rival_pais: cruce.rival_pais, gf: gh, gc: ga, goles: convocado ? golesJugador : 0, asistencias: convocado ? asistenciasJugador : 0, penales, prorroga });

        const rondaJugada = m.ko_ronda;
        const jugadorPaso = ganador_pais === paisJ;
        const eraFinal = rondaJugada === 'final';

        // Avanzar bracket: simular los otros partidos de la ronda y armar próxima.
        avanzarRondaKO(m, paisJ, career.seed || 1, career.seleccion.ciclo);
        if (jugadorPaso && eraFinal) {
            m.campeon_pais = paisJ;
            m.fase = 'finalizado';
            m.llego_a = 'campeon';
        } else if (!jugadorPaso) {
            m.llego_a = rondaJugada;
            // El jugador queda eliminado. Si no era la final, dejamos correr el torneo en modo
            // espectador para que vea cómo termina (con el cuadro). Si era la final, ya quedó
            // 'finalizado' (es subcampeón) por avanzarRondaKO.
            if (!eraFinal) m.fase = 'eliminado_espectador';
        } else if (!eraFinal) {
            m.llego_a = rondaJugada; // avanza, esta sigue siendo su "última ronda alcanzada" actual
            // sigue en ko_vivos para la próxima ronda
        }
        return armarYRetornar({
            competicion: 'mundial-ko',
            rival_nombre: rivalSel.nombre, rival_pais: cruce.rival_pais,
            local: true, gf: gh, gc: ga,
            minutos_jugados: convocado ? minutosJugador : 0,
            goles_jugador: convocado ? golesJugador : 0,
            asistencias_jugador: convocado ? asistenciasJugador : 0,
            amarilla: convocado ? amarilla : false,
            roja: convocado ? roja : false,
            no_convocado: !convocado,
            ronda: rondaJugada, penales, prorroga, penalShootout,
            avanzo: jugadorPaso, campeon: jugadorPaso && eraFinal,
            ciclo: career.seleccion.ciclo,
        });
    }

    // Si fase === 'finalizado' o 'no_clasif', no hay más partidos: caller debe finalizar.
    return null;
}

// "Mejor tercero del mundial": 8 mejores de los 12 terceros van a 16avos.
function esMejorTerceroMundial(mundial, paisJ) {
    const terceros = mundial.grupos.map(g => {
        const tabla = ordenarStandings(g.standings);
        return tabla[2] ? { ...tabla[2], grupo: g.indice } : null;
    }).filter(Boolean).sort((a, b) => (b.pts - a.pts) || ((b.gf - b.gc) - (a.gf - a.gc)));
    return terceros.slice(0, 8).some(t => t.pais === paisJ);
}

// Finaliza el mundial: push al histórico, limpia el state mundial. Reusable cuando el jugador
// terminó (eliminado o campeón).
function finalizarMundial(career) {
    const m = career.seleccion.mundial;
    if (!m) return;
    // Participación REAL del jugador (no que su selección haya jugado). Los logros se basan en esto.
    const convocadoEnFinales = (m.partidosJugadorConvocado || 0) > 0;
    // ¿Fue convocado en algún momento del ciclo (clasificatorias o Mundial)? Sirve para "clasificar
    // a un Mundial con tu selección": jugaste las eliminatorias y tu país entró.
    const convocadoEnCiclo = ((career.seleccion.stats_ciclo?.partidos || 0) > 0) || convocadoEnFinales;
    const golesKO = m.partidosJugador.filter(p => p.tipo === 'ko').reduce((a, p) => a + (p.goles || 0), 0);
    const golesFinal = m.partidosJugador.filter(p => p.tipo === 'ko' && p.ronda === 'final').reduce((a, p) => a + (p.goles || 0), 0);
    career.mundiales_historicos.push({
        ciclo: career.seleccion.ciclo,
        edad_jugador: career.edad,
        pais_jugador: career.seleccion.pais,
        jugador_convocado: convocadoEnFinales,
        jugador_jugo: convocadoEnFinales,
        jugador_convocado_ciclo: convocadoEnCiclo,
        partidos: m.partidosJugadorConvocado || 0,
        goles: m.partidosJugador.reduce((a, p) => a + (p.goles || 0), 0),
        goles_ko: golesKO,
        goles_final: golesFinal,
        asistencias: m.partidosJugador.reduce((a, p) => a + (p.asistencias || 0), 0),
        llego_a: m.llego_a || 'grupos',
        campeon_pais: m.campeon_pais || '',
        subcampeon_pais: m.subcampeon_pais || '',
    });
    career.seleccion_carrera.mundiales_disputados += 1;
    career.seleccion.mundial = null;
}

// Helper local: imports nombrados arriba (umbralCallup ya viene como `umbralCallup`).
function umbralCallupSel(seleccion) {
    const THRESH = { 5: 82, 4.5: 80, 4: 77, 3.5: 75, 3: 72, 2.5: 68, 2: 65, 1.5: 60, 1: 0 };
    return THRESH[seleccion?.estrellas] ?? 0;
}

// ── SELECCIÓN: ciclo Mundial cada 4 años ───────────────────────────────────
// edad 18 → ciclo 1 año 1; edad 21 → ciclo 1 año 4 (Mundial); edad 22 → ciclo 2 año 1; etc.
// 5 mundiales por carrera (edades 21, 25, 29, 33, 37 con retiro a los 39).
function cicloDeEdad(edad) {
    const desde = (edad || 18) - 18;
    return {
        ciclo: Math.floor(desde / 4) + 1,
        añoCiclo: (desde % 4) + 1, // 1,2,3 = clasif; 4 = mundial
    };
}

// Inicializa el estado de selección si todavía no existe O si arrancamos un ciclo nuevo.
// Idempotente: si ya hay state del ciclo correcto, no hace nada.
function initSeleccionSiHaceFalta(career) {
    const sel = seleccionDelJugador(career);
    if (!sel) return; // nacionalidad sin selección → feature invisible
    const { ciclo, añoCiclo } = cicloDeEdad(career.edad);
    if (career.seleccion && career.seleccion.ciclo === ciclo) {
        // Mismo ciclo en curso: solo actualizar el año en curso (idempotente).
        career.seleccion.año_ciclo = añoCiclo;
        return;
    }
    // Nuevo ciclo (o primera vez): generar sorteo + calendario.
    const state = iniciarCicloClasif(sel, career.seed || 1, ciclo);
    state.año_ciclo = añoCiclo;
    state.turno_jornada = -1; // jornada cuyo "turno de selección" YA se jugó (como euro_turno_jornada).
    career.seleccion = state;
}

function runTurn(career) {
        // Migración de partidas previas a la feature de contratos: si no tienen contrato
        // cargado, asumimos uno de 3 temporadas en curso para no forzar un vencimiento falso.
        if (!career.contrato_anios) {
            career.contrato_anios = 3;
            career.contrato_restante = 3;
        }

        // ── Tracking de LOGROS (ver career/logros.js) ──
        // Estado acumulado de la carrera para detectar logros que no se pueden recomputar después
        // (remontadas, ligas/clubes por los que pasó, invictos en copa). Se mantiene acá durante
        // la simulación; el evaluador lo lee en cada avance.
        if (!career.logros_track) career.logros_track = { remonto_0_3: false, ligas_jugadas: [], clubs_5est: [] };
        if (!career.titulos_detalle) career.titulos_detalle = [];
        if (!career.copa_perdida_temporada) career.copa_perdida_temporada = {};
        if (!career.mundiales_historicos) career.mundiales_historicos = [];
        if (!career.seleccion_carrera) career.seleccion_carrera = { partidos: 0, goles: 0, asistencias: 0, mundiales_disputados: 0, amarillas: 0, rojas: 0 };
        // Selección: lazy init del ciclo (si la nacionalidad tiene selección).
        initSeleccionSiHaceFalta(career);
        // Liga/club actuales en el registro (dedup) para "5 grandes ligas" y "3 clubes 5★".
        const lt = career.logros_track;
        if (career.club_actual) {
            const yaLiga = lt.ligas_jugadas.some(l => l.pais === career.club_actual.pais && l.division === career.club_actual.division);
            if (!yaLiga) lt.ligas_jugadas.push({ pais: career.club_actual.pais, division: career.club_actual.division });
            if ((career.club_actual.estrellas || 0) >= 5 && !lt.clubs_5est.includes(career.club_actual.tm_id)) {
                lt.clubs_5est.push(career.club_actual.tm_id);
            }
        }

        const playerLeague = career.leagues.find(
            l => l.pais === career.club_actual.pais && l.division === career.club_actual.division
        );
        if (!playerLeague) { const e = new Error('Liga del jugador no encontrada.'); e.status = 404; throw e; }

        const totalJornadas = roundsForCount(playerLeague.clubs.length);
        const nextJornada = career.jornada_actual + 1;

        if (nextJornada > totalJornadas) {
            // (El Mundial se juega a MITAD del año 4, no acá; ver bloque "MUNDIAL (mitad de la
            // temporada del año 4)" más abajo.)
            const playerClub = playerLeague.clubs.find(c => c.tm_id === career.club_actual.tm_id);
            const tabla = sortStandings(playerLeague.clubs);
            const posicion_final = tabla.findIndex(c => c.tm_id === career.club_actual.tm_id) + 1;

            // Media evoluciona según atributos+físico (nivel implícito), cómo le fue al equipo
            // y al jugador, y la edad. Y se ganan puntos de atributo según edad + desempeño.
            const delta = PM.computeMediaDelta({
                posicion: career.posicion,
                edad: career.edad,
                media: career.rating,
                stats: career.stats_temporada,
                ownClub: playerClub,
                atributos: career.atributos,
                altura: career.altura,
                peso: career.peso,
            });
            const rating_anterior = career.rating;
            career.rating = clamp(rating_anterior + delta, 40, 99);

            const desempeno = PM.desempenoTemporada({
                posicion: career.posicion,
                stats: career.stats_temporada,
                ownClub: playerClub,
                altura: career.altura,
                peso: career.peso,
            });
            const puntosGanados = PM.puntosTemporada(career.edad, desempeno);
            career.puntos_disponibles = (career.puntos_disponibles || 0) + puntosGanados;

            // Títulos ganados esta temporada
            const titulosTemporada = [];
            const campeonLiga = tabla[0];
            if (campeonLiga?.tm_id === career.club_actual.tm_id) {
                titulosTemporada.push(`Campeón ${playerLeague.nombre}`);
                // Detalle para LOGROS: la liga la ganó el jugador → guardamos país, estrellas,
                // si fue invicto (pp del campeón === 0) y el tamaño de la liga.
                career.titulos_detalle.push({
                    temporada: career.temporada,
                    tipo: 'liga',
                    club_nombre: career.club_actual.nombre,
                    club_logo: career.club_actual.logo,
                    pais: career.club_actual.pais,
                    estrellas: career.club_actual.estrellas,
                    invicto: (campeonLiga.pp || 0) === 0,
                    liga_equipos: playerLeague.clubs.length,
                    division: career.club_actual.division,
                });
            }
            // Palmarés global de la liga (no solo del jugador): se registra al campeón
            career.palmares.push({
                temporada: career.temporada,
                titulo: `Campeón ${playerLeague.nombre}`,
                club: campeonLiga?.nombre || '',
            });

            // Champions / Europa: campeón = ganador de la final (fase eliminatoria).
            // Si por temporada corta no se llegó a jugar la final, cae al 1° de la fase de liga.
            for (const [comp, label, tipo, field] of [
                [career.champions_league, 'Champions League', 'champions', 'champions'],
                [career.europa_league, 'Europa League', 'europa', 'europa'],
                [career.libertadores, 'Copa Libertadores', 'libertadores', 'libertadores'],
                [career.sudamericana, 'Copa Sudamericana', 'sudamericana', 'sudamericana'],
            ]) {
                if (!comp || !comp.clubs || comp.clubs.length === 0) continue;
                let campeon = comp.campeon_tm_id ? comp.clubs.find(c => c.tm_id === comp.campeon_tm_id) : null;
                if (!campeon) campeon = sortStandings(comp.clubs)[0];
                if (!campeon) continue;
                career.palmares.push({ temporada: career.temporada, titulo: `Campeón ${label}`, club: campeon.nombre });
                if (campeon.tm_id === career.club_actual.tm_id) {
                    titulosTemporada.push(`Campeón ${label}`);
                    // Detalle para LOGROS: invicto = el jugador no perdió ningún partido de esa
                    // copa esta temporada (copa_perdida_temporada se marca en cada derrota de copa).
                    career.titulos_detalle.push({
                        temporada: career.temporada,
                        tipo,
                        club_nombre: career.club_actual.nombre,
                        club_logo: career.club_actual.logo,
                        pais: career.club_actual.pais,
                        estrellas: career.club_actual.estrellas,
                        invicto: !career.copa_perdida_temporada[field],
                        liga_equipos: 0,
                    });
                }
            }

            // Stats por temporada para el palmarés / pestaña de carrera
            career.historial_stats.push({
                temporada: career.temporada,
                club_nombre: career.club_actual.nombre,
                club_logo: career.club_actual.logo,
                liga_nombre: playerLeague.nombre,
                division: career.club_actual.division,
                posicion_final,
                partidos: career.stats_temporada.partidos,
                goles: career.stats_temporada.goles,
                asistencias: career.stats_temporada.asistencias,
                minutos_totales: career.stats_temporada.minutos_totales,
                rating_final: career.rating,
                titulos: titulosTemporada,
            });

            // Retiro obligatorio: 39 años es la última temporada jugable. Si el jugador acaba
            // de terminar su temporada con 39, cuelga los botines (no avanza a otra temporada).
            if (career.edad >= 39) {
                return {
                    career,
                    partidoJugador: null,
                    temporadaFinalizada: false,
                    retirado: true,
                    resumenTemporada: {
                        temporada_anterior: career.temporada,
                        posicion_final,
                        rating_anterior,
                        rating_nuevo: career.rating,
                        delta,
                        puntos_ganados: puntosGanados,
                        ascendio: false,
                        descendio: false,
                        nueva_division: career.club_actual.division,
                        stats: { ...career.stats_temporada },
                        titulos: titulosTemporada,
                        nuevo_valor_mercado: career.valor_mercado,
                        retirado: true,
                    },
                };
            }

            // El contrato corre una temporada. Si se venció, el club decide si quiere
            // renovar o no:
            //  - Si quiere → ofrece renovación + el jugador igual puede irse a otro club.
            //  - Si NO quiere → no hay renovación: el jugador está obligado a fichar por
            //    uno de los clubes que lo pretenden (varias ofertas externas).
            // Si el contrato sigue vigente, las ofertas que lleguen son opcionales.
            career.contrato_restante = Math.max(0, (career.contrato_restante || 0) - 1);
            const contratoVencido = career.contrato_restante <= 0;
            const edadProximaTemporada = career.edad + 1;
            const clubRenueva = contratoVencido && clubWantsRenewal({
                rating: career.rating,
                edad: edadProximaTemporada,
                estrellas: career.club_actual?.estrellas,
            });

            const ofertas = generateTransferOffers({
                leagues: career.leagues,
                clubActual: career.club_actual,
                rating: career.rating,
                edad: edadProximaTemporada,
                withPosicion: true,
                posicionFinal: true,
                // Si el club no renueva, garantizamos varias opciones para que el jugador
                // siempre tenga a dónde ir; si renueva, alcanza con una externa.
                minimo: contratoVencido ? (clubRenueva ? 1 : 2) : 0,
            });
            if (clubRenueva) {
                ofertas.unshift(buildRenewalOffer({
                    leagues: career.leagues,
                    clubActual: career.club_actual,
                    rating: career.rating,
                }));
            }
            career.ofertas_transferencia = ofertas;

            // Cupos y ascensos/descensos se deciden con la tabla de cada liga del país. En modo
            // determinístico, las ligas ajenas no están guardadas → worldOpts hace que se
            // recalculen con worldSim sobre la temporada que termina; la liga del jugador usa su
            // tabla real. En legacy (seed 0) worldOpts es inerte y se usa la tabla guardada.
            const wo = worldOpts(career);
            const { champions, europa } = selectEuropeanPools(career.leagues, wo);
            career.champions_league = buildEuropeanCompetition(champions);
            career.europa_league = buildEuropeanCompetition(europa);

            const { libertadores, sudamericana } = selectConmebolPools(career.leagues, wo);
            career.libertadores = buildConmebolCompetition(libertadores);
            career.sudamericana = buildConmebolCompetition(sudamericana);

            const { ascendio, descendio } = applyPromotionRelegation(career.leagues, career.club_actual, wo);
            // El club_actual mantiene su nombre pero su división puede haber cambiado: refrescar estrellas/logo
            const refreshedLeague = career.leagues.find(l => l.pais === career.club_actual.pais && l.division === career.club_actual.division);
            const refreshedClub = refreshedLeague?.clubs.find(c => c.tm_id === career.club_actual.tm_id);
            if (refreshedClub) {
                career.club_actual.estrellas = refreshedClub.estrellas;
            }

            startNewSeason(career.leagues);
            // Calendario nuevo SOLO para la liga del jugador; el resto se simula a tabla final.
            setupSeasonCalendars(career);

            const stats_temporada_anterior = { ...career.stats_temporada };
            const temporada_anterior = career.temporada;

            // ── SELECCIÓN: cierre del año del ciclo ──
            // Al cerrar el año 3 (último de clasif): simular fondo + determinar clasificación.
            // Stage 4 va a tirar el Mundial al cerrar el año 4. Para Stage 3, año 4 no tiene partidos
            // de selección agendados → solo registramos un placeholder en mundiales_historicos.
            let seleccionResumen = null;
            if (career.seleccion) {
                const yearEnd = career.seleccion.año_ciclo;
                if (yearEnd === 3 && career.seleccion.fase === 'eliminado') {
                    // Ya quedó eliminado antes (p. ej. pre-repechaje CONCACAF perdido): no recalcular
                    // la clasificación, pero SÍ simular los grupos para que el Mundial espectador
                    // refleje la confederación de verdad (sino quedan standings vacíos).
                    if ((career.seleccion.grupos || []).length) procesarFinDeAño(career.seleccion, 3, career.seed || 1);
                    seleccionResumen = { fase: 'eliminado', ciclo: career.seleccion.ciclo };
                } else if (yearEnd === 3) {
                    procesarFinDeAño(career.seleccion, 3, career.seed || 1);
                    determinarClasificacion(career.seleccion);
                    // Si fue a playoff, NO se resuelve solo: se juega turn-by-turn durante el año 4.
                    // Construimos el camino del jugador (rivales + partidos) para jugarlo.
                    if (career.seleccion.fase === 'playoff') {
                        career.seleccion.playoff = construirPlayoffJugador(career.seleccion, career.seed || 1);
                        career.seleccion.playoff_turno_jornada = -1;
                        // Si por algún motivo no hay camino (pool vacío), cae a eliminado.
                        if (!career.seleccion.playoff) career.seleccion.fase = 'eliminado';
                    }
                    seleccionResumen = {
                        fase: career.seleccion.fase,
                        posicion_grupo: career.seleccion.posicion_grupo,
                        ciclo: career.seleccion.ciclo,
                        a_playoff: career.seleccion.fase === 'playoff',
                    };
                } else if (yearEnd === 4) {
                    // El Mundial ya se jugó turno-a-turno antes de llegar acá. finalizarMundial()
                    // ya empujó al histórico. Si el jugador no clasificó (no hubo mundial), igual
                    // registramos el ciclo en el histórico para los logros y la imagen de retiro.
                    const yaRegistrado = career.mundiales_historicos.some(h => h.ciclo === career.seleccion.ciclo);
                    if (!yaRegistrado) {
                        career.mundiales_historicos.push({
                            ciclo: career.seleccion.ciclo,
                            edad_jugador: career.edad,
                            pais_jugador: career.seleccion.pais,
                            jugador_convocado: false, jugador_jugo: false,
                            jugador_convocado_ciclo: (career.seleccion.stats_ciclo?.partidos || 0) > 0,
                            partidos: 0, goles: 0, goles_ko: 0, goles_final: 0, asistencias: 0,
                            llego_a: 'no_clasif', campeon_pais: '', subcampeon_pais: '',
                        });
                    }
                    const ult = career.mundiales_historicos[career.mundiales_historicos.length - 1];
                    seleccionResumen = { mundialRegistrado: true, llego_a: ult.llego_a, ciclo: career.seleccion.ciclo, campeon_pais: ult.campeon_pais, partidos: ult.partidos, goles: ult.goles };
                } else {
                    seleccionResumen = { fase: 'en_curso', año_ciclo: yearEnd, ciclo: career.seleccion.ciclo };
                }
            }

            career.temporada += 1;
            career.edad += 1;
            career.jornada_actual = 0;
            career.mes = 7;
            career.suspendido_jornadas = 0;
            career.suspendido_copa_jornadas = 0;
            career.copa_perdida_temporada = {}; // reset del tracking de invicto en copa (LOGROS)
            career.ultima_copa = '';
            career.stats_temporada = { partidos: 0, goles: 0, asistencias: 0, minutos_totales: 0 };
            career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
            career.euro_turno_jornada = -1;
            career.proximo_turno_es_euro = false;
            // Reset del turno de selección y refresh del año del ciclo (lazy init detectará si arranca
            // ciclo nuevo y sorteará grupos de nuevo).
            if (career.seleccion) career.seleccion.turno_jornada = -1;
            initSeleccionSiHaceFalta(career);

            return {
                career,
                partidoJugador: null,
                temporadaFinalizada: false,
                resumenTemporada: {
                    temporada_anterior,
                    posicion_final,
                    rating_anterior,
                    rating_nuevo: career.rating,
                    delta,
                    puntos_ganados: puntosGanados,
                    seleccion: seleccionResumen,
                    ascendio,
                    descendio,
                    nueva_division: career.club_actual.division,
                    stats: stats_temporada_anterior,
                    titulos: titulosTemporada,
                    nuevo_valor_mercado: career.valor_mercado,
                    contrato_vencido: contratoVencido,
                    contrato_restante: career.contrato_restante,
                    club_renueva: clubRenueva,
                },
            };
        }

        const sel = career.seleccion;

        // ===== Turno de PRE-REPECHAJE CONCACAF (bombo 4, año 1, antes de los grupos) =====
        // 2 rondas a partido único en jornadas 2 y 6. Ganás las dos → entrás a los grupos
        // (fase 'clasif'); perdés una → eliminado.
        if (sel && sel.año_ciclo === 1 && sel.fase === 'preplayoff' && sel.preplayoff && sel.preplayoff.fase === 'jugando'
            && sel.preplayoff_turno_jornada !== nextJornada) {
            const pp = sel.preplayoff;
            const jornadaTie = 2 + pp.idx * 4; // j2, j6
            if (nextJornada === jornadaTie && pp.idx < pp.ties.length) {
                const tie = pp.ties[pp.idx];
                const selRef = seleccionDelJugador(career);
                const convocado = isCalledUp(career, selRef);
                const rivalSel = SELECCIONES_BY_PAIS[tie.rival_pais];
                const miClub = { tm_id: sel.pais, nombre: selRef.nombre, estrellas: selRef.estrellas };
                const rivClub = { tm_id: tie.rival_pais, nombre: rivalSel.nombre, estrellas: rivalSel.estrellas };
                let result = convocado
                    ? simulateMatch(miClub, rivClub, { rating: PM.efectiveRating(career.rating, career.moral), posicion: career.posicion, mentalidad: career.mentalidad, tacticas: career.tacticas, rivalTacticas: rivalTacticas(rivClub, `preplay-${sel.ciclo}-${pp.idx}`), isHome: true })
                    : simulateMatch(miClub, rivClub, null);
                if (convocado) {
                    sel.stats_ciclo.partidos += 1;
                    sel.stats_ciclo.goles += result.golesJugador;
                    sel.stats_ciclo.asistencias += result.asistenciasJugador;
                    career.seleccion_carrera.partidos += 1;
                    career.seleccion_carrera.goles += result.golesJugador;
                    career.seleccion_carrera.asistencias += result.asistenciasJugador;
                    if (result.amarilla) career.seleccion_carrera.amarillas += 1;
                    if (result.roja) career.seleccion_carrera.rojas += 1;
                }
                let gh = result.gh, ga = result.ga;
                if (gh === ga) { if (Math.random() < 0.5) gh++; else ga++; } // sin empate (penales)
                tie.gf = gh; tie.gc = ga; tie.jugado = true;
                let resuelto = null;
                if (gh > ga) {
                    pp.idx += 1;
                    if (pp.idx >= pp.ties.length) { pp.fase = 'clasificado'; sel.fase = 'clasif'; resuelto = 'a_grupos'; }
                } else {
                    pp.fase = 'eliminado'; sel.fase = 'eliminado'; resuelto = 'eliminado';
                }
                sel.preplayoff_turno_jornada = nextJornada;
                career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
                const partidoSeleccion = {
                    competicion: 'preplayoff',
                    rival_nombre: rivalSel.nombre, rival_pais: tie.rival_pais,
                    local: true, gf: gh, gc: ga,
                    minutos_jugados: convocado ? result.minutosJugador : 0,
                    goles_jugador: convocado ? result.golesJugador : 0,
                    asistencias_jugador: convocado ? result.asistenciasJugador : 0,
                    amarilla: convocado ? result.amarilla : false,
                    roja: convocado ? result.roja : false,
                    no_convocado: !convocado,
                    ronda: tie.ronda, resuelto,
                    ciclo: sel.ciclo,
                };
                pushHistorialSeleccion(career, partidoSeleccion);
                return { career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion, suspendido: false, esTurnoEuro: false, esTurnoSeleccion: true, temporadaFinalizada: false };
            }
        }

        // ===== Turno de SELECCIÓN (clasificatorias) =====
        // Solo si: hay selección, estamos en año 1-3 del ciclo (clasif), todavía no clasificó/eliminó,
        // hay un partido del jugador asignado a esta jornada, y no se jugó ya en este turno.
        const esAñoClasif = sel && sel.año_ciclo >= 1 && sel.año_ciclo <= 3 && sel.fase === 'clasif';
        if (esAñoClasif && sel.turno_jornada !== nextJornada) {
            const schedule = seleccionScheduleEnSeason(sel, sel.año_ciclo, totalJornadas);
            const partidoIdx = schedule.get(nextJornada);
            if (partidoIdx != null) {
                const partido = sel.calendarioJugador[partidoIdx];
                if (partido && !partido.jugado) {
                    // ¿Convocado? Si la media supera el umbral, juega; si no, el rival juega solo.
                    const seleccionRef = seleccionDelJugador(career);
                    const convocado = isCalledUp(career, seleccionRef);
                    const homeClub = { tm_id: partido.home.pais, nombre: partido.home.nombre, estrellas: partido.home.estrellas };
                    const awayClub = { tm_id: partido.away.pais, nombre: partido.away.nombre, estrellas: partido.away.estrellas };
                    let result;
                    if (convocado) {
                        const player = {
                            rating: PM.efectiveRating(career.rating, career.moral),
                            posicion: career.posicion,
                            mentalidad: career.mentalidad,
                            tacticas: career.tacticas,
                            rivalTacticas: rivalTacticas(awayClub, `sel-${sel.ciclo}-${sel.año_ciclo}-${partidoIdx}`),
                            isHome: partido.local,
                        };
                        result = simulateMatch(homeClub, awayClub, player);
                    } else {
                        // No convocado: el rival y el equipo juegan sin él.
                        result = simulateMatch(homeClub, awayClub, null);
                    }
                    aplicarPartidoJugador(sel, partidoIdx, {
                        gh: result.gh, ga: result.ga,
                        minutos_jugados: convocado ? result.minutosJugador : 0,
                        goles_jugador: convocado ? result.golesJugador : 0,
                        asistencias_jugador: convocado ? result.asistenciasJugador : 0,
                        amarilla: convocado ? result.amarilla : false,
                        roja: convocado ? result.roja : false,
                        no_convocado: !convocado,
                    });
                    // Que los demás países del grupo también jueguen (no solo el jugador).
                    refrescarStandingsGrupoJugador(sel, career.seed || 1);
                    if (convocado) {
                        career.seleccion_carrera.partidos += 1;
                        career.seleccion_carrera.goles += result.golesJugador;
                        career.seleccion_carrera.asistencias += result.asistenciasJugador;
                        if (result.amarilla) career.seleccion_carrera.amarillas += 1;
                        if (result.roja) career.seleccion_carrera.rojas += 1;
                    }
                    sel.turno_jornada = nextJornada;
                    const partidoSeleccion = {
                        competicion: 'clasificatoria',
                        rival_nombre: partido.rival.nombre,
                        rival_pais: partido.rival.pais,
                        local: partido.local,
                        gf: partido.local ? result.gh : result.ga,
                        gc: partido.local ? result.ga : result.gh,
                        minutos_jugados: convocado ? result.minutosJugador : 0,
                        goles_jugador: convocado ? result.golesJugador : 0,
                        asistencias_jugador: convocado ? result.asistenciasJugador : 0,
                        amarilla: convocado ? result.amarilla : false,
                        roja: convocado ? result.roja : false,
                        no_convocado: !convocado,
                        ronda: partido.ronda,
                        año_ciclo: sel.año_ciclo,
                        ciclo: sel.ciclo,
                    };
                    pushHistorialSeleccion(career, partidoSeleccion);
                    return { career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion, suspendido: false, esTurnoEuro: false, esTurnoSeleccion: true, temporadaFinalizada: false };
                }
            }
        }

        // ===== Turno de REPECHAJE (año 4, antes del Mundial) =====
        // Si el jugador quedó en repechaje, juega su camino turn-by-turn al inicio del año 4. Los
        // partidos van en jornadas tempranas (3 + idx*5). Al ganar todos → clasificado; al perder
        // → eliminado. CONCACAF es ida+vuelta (se decide por global).
        if (sel && sel.año_ciclo === 4 && sel.fase === 'playoff' && sel.playoff && sel.playoff.fase === 'jugando'
            && sel.playoff_turno_jornada !== nextJornada) {
            const po = sel.playoff;
            const jornadaTie = 3 + po.idx * 5;
            if (nextJornada === jornadaTie && po.idx < po.ties.length) {
                const tie = po.ties[po.idx];
                const selRef = seleccionDelJugador(career);
                const convocado = isCalledUp(career, selRef);
                const rivalSel = SELECCIONES_BY_PAIS[tie.rival_pais];
                // El jugador es local en ida/single, visitante en vuelta.
                const esLocal = tie.leg !== 'vuelta';
                const miClub = { tm_id: sel.pais, nombre: selRef.nombre, estrellas: selRef.estrellas };
                const rivClub = { tm_id: tie.rival_pais, nombre: rivalSel.nombre, estrellas: rivalSel.estrellas };
                const homeClub = esLocal ? miClub : rivClub;
                const awayClub = esLocal ? rivClub : miClub;
                let result;
                if (convocado) {
                    const player = {
                        rating: PM.efectiveRating(career.rating, career.moral),
                        posicion: career.posicion, mentalidad: career.mentalidad, tacticas: career.tacticas,
                        rivalTacticas: rivalTacticas(rivClub, `playoff-${sel.ciclo}-${po.idx}-${tie.rival_pais}`),
                        isHome: esLocal,
                    };
                    result = simulateMatch(homeClub, awayClub, player);
                } else {
                    result = simulateMatch(homeClub, awayClub, null);
                }
                const miGf = esLocal ? result.gh : result.ga;
                const miGc = esLocal ? result.ga : result.gh;
                if (convocado) {
                    sel.stats_ciclo.partidos += 1;
                    sel.stats_ciclo.goles += result.golesJugador;
                    sel.stats_ciclo.asistencias += result.asistenciasJugador;
                    career.seleccion_carrera.partidos += 1;
                    career.seleccion_carrera.goles += result.golesJugador;
                    career.seleccion_carrera.asistencias += result.asistenciasJugador;
                    if (result.amarilla) career.seleccion_carrera.amarillas += 1;
                    if (result.roja) career.seleccion_carrera.rojas += 1;
                }
                // Guardamos el resultado en el tie para poder dibujar el bracket.
                tie.gf = miGf; tie.gc = miGc; tie.jugado = true;
                // Resolución de la llave.
                let resueltoFase = null; // 'clasificado' | 'eliminado' | null (sigue)
                if (tie.leg === 'single') {
                    let gano = miGf > miGc;
                    if (miGf === miGc) {
                        // Empate a partido único: se define por penales (tanda real).
                        const tanda = simulatePenaltyShootout(selRef.estrellas, rivalSel.estrellas);
                        tie.penales = true; tie.penalShootout = tanda;
                        gano = tanda.ganador === 'a';
                    }
                    if (gano) {
                        po.idx += 1;
                        if (po.idx >= po.ties.length) resueltoFase = 'clasificado';
                    } else {
                        resueltoFase = 'eliminado';
                    }
                } else if (tie.leg === 'ida') {
                    po.agg_favor += miGf; po.agg_contra += miGc;
                    po.idx += 1; // pasa a la vuelta
                } else if (tie.leg === 'vuelta') {
                    po.agg_favor += miGf; po.agg_contra += miGc;
                    let gano = po.agg_favor > po.agg_contra;
                    if (po.agg_favor === po.agg_contra) {
                        // Global empatado tras ida y vuelta: se define por penales (tanda real).
                        const tanda = simulatePenaltyShootout(selRef.estrellas, rivalSel.estrellas);
                        tie.penales = true; tie.penalShootout = tanda;
                        gano = tanda.ganador === 'a';
                    }
                    if (gano) {
                        po.eliminados = po.eliminados || [];
                        po.eliminados.push(tie.rival_pais);
                        if (tie.ronda === 'Semifinal' && po.otrosParaFinal) {
                            // Ganó la semifinal: recién ahora se resuelve (y se revela) el rival
                            // de la Final, simulando la OTRA semifinal del mismo grupo de 4.
                            const finalRival = resolverRivalFinal(po.otrosParaFinal, career.seed || 1, sel.ciclo, sel.confederacion);
                            if (finalRival) {
                                po.agg_favor = 0; po.agg_contra = 0; // el global se resetea para la nueva llave
                                po.ties.push(
                                    { ronda: 'Final', rival_pais: finalRival.pais, rival_nombre: finalRival.nombre, leg: 'ida' },
                                    { ronda: 'Final', rival_pais: finalRival.pais, rival_nombre: finalRival.nombre, leg: 'vuelta' },
                                );
                            } else {
                                resueltoFase = 'clasificado'; // defensivo: no había rival para la final
                            }
                        } else {
                            resueltoFase = 'clasificado';
                        }
                    } else {
                        resueltoFase = 'eliminado';
                    }
                    po.idx += 1;
                }
                if (resueltoFase) { po.fase = resueltoFase; sel.fase = resueltoFase; }
                sel.playoff_turno_jornada = nextJornada;

                career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
                const partidoSeleccion = {
                    competicion: 'playoff',
                    rival_nombre: rivalSel.nombre, rival_pais: tie.rival_pais,
                    local: esLocal, gf: miGf, gc: miGc,
                    minutos_jugados: convocado ? result.minutosJugador : 0,
                    goles_jugador: convocado ? result.golesJugador : 0,
                    asistencias_jugador: convocado ? result.asistenciasJugador : 0,
                    amarilla: convocado ? result.amarilla : false,
                    roja: convocado ? result.roja : false,
                    no_convocado: !convocado,
                    ronda: tie.ronda, leg: tie.leg,
                    penales: !!tie.penales, penalShootout: tie.penalShootout || null,
                    agg_favor: po.agg_favor, agg_contra: po.agg_contra,
                    resuelto: resueltoFase, // 'clasificado'|'eliminado'|null
                    ciclo: sel.ciclo,
                };
                pushHistorialSeleccion(career, partidoSeleccion);
                return { career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion, suspendido: false, esTurnoEuro: false, esTurnoSeleccion: true, temporadaFinalizada: false };
            }
        }

        // ===== MUNDIAL (mitad de la temporada del año 4) =====
        // El Mundial se juega a MITAD del año 4 (no al cierre). Al llegar a la jornada del Mundial,
        // se pausa la liga: primero un turno de PRESENTACIÓN (convocatoria + arma el cuadro, sin
        // jugar), después un turno por cada partido (grupos + KO), y al terminar la liga sigue.
        if (sel && sel.año_ciclo === 4 && !sel.mundial_disputado && nextJornada >= Math.ceil(totalJornadas / 2)) {
            if (sel.fase === 'clasificado') {
                if (!sel.mundial) {
                    // Turno de PRESENTACIÓN: arma el bracket y muestra la convocatoria ANTES de jugar.
                    sel.mundial = iniciarMundialPlayer(seleccionDelJugador(career), sel.pais, career.seed || 1, sel.ciclo, sel);
                    const convocado = isCalledUp(career, seleccionDelJugador(career));
                    career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
                    return {
                        career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion: null,
                        esMundialIntro: true, convocadoMundial: convocado, paisMundial: sel.pais, ciclo: sel.ciclo,
                        suspendido: false, esTurnoEuro: false, esTurnoSeleccion: false, temporadaFinalizada: false,
                    };
                }
                if (sel.mundial.fase === 'grupos' || sel.mundial.fase === 'ko') {
                    return jugarSiguientePartidoMundial(career);
                }
                if (sel.mundial.fase === 'eliminado_espectador') {
                    // Quedaste eliminado: el torneo sigue solo (una ronda por turno) hasta el campeón.
                    const step = avanzarMundialEliminado(sel.mundial, career.seed || 1, sel.ciclo);
                    career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
                    return {
                        career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion: null,
                        esMundialEliminadoEspectador: true,
                        mundialEliminadoStep: { ...step, campeon: sel.mundial.campeon_pais, subcampeon: sel.mundial.subcampeon_pais, ciclo: sel.ciclo },
                        suspendido: false, esTurnoEuro: false, esTurnoSeleccion: false, temporadaFinalizada: false,
                    };
                }
                if (sel.mundial.fase === 'finalizado') {
                    finalizarMundial(career);
                    sel.mundial_disputado = true;
                    // fall-through: seguimos con la jornada de liga normal.
                }
            } else {
                // No clasificaste: espectador paso a paso (también a mitad del año 4).
                if (!sel.mundial_espectador) sel.mundial_espectador = iniciarMundialEspectador(career.seed || 1, sel.ciclo, sel);
                const esp = sel.mundial_espectador;
                if (esp.fase !== 'finalizado') {
                    const step = avanzarMundialEspectador(esp, career.seed || 1, sel.ciclo);
                    career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
                    return {
                        career, partidoJugador: null, partidoEuropeo: null, partidoSeleccion: null,
                        esEspectadorMundial: true,
                        mundialEspectadorStep: { ...step, campeon: esp.campeon, subcampeon: esp.subcampeon, ciclo: sel.ciclo },
                        suspendido: false, esTurnoEuro: false, esTurnoSeleccion: false, temporadaFinalizada: false,
                    };
                }
                // finalizado → registramos el ciclo (no clasificó) una sola vez y seguimos.
                if (!career.mundiales_historicos.some(h => h.ciclo === sel.ciclo)) {
                    career.mundiales_historicos.push({
                        ciclo: sel.ciclo, edad_jugador: career.edad, pais_jugador: sel.pais,
                        jugador_convocado: false, jugador_jugo: false,
                        jugador_convocado_ciclo: (sel.stats_ciclo?.partidos || 0) > 0,
                        partidos: 0, goles: 0, goles_ko: 0, goles_final: 0, asistencias: 0,
                        llego_a: 'no_clasif', campeon_pais: '', subcampeon_pais: '',
                    });
                }
                sel.mundial_disputado = true;
                // fall-through.
            }
        }

        // ===== Turno europeo (separado de la jornada de liga) =====
        // Los partidos de copa del jugador se juegan en su PROPIO turno, como la fecha de
        // mitad de semana: no se "pisan" con el partido de liga. Si el próximo avance cae en
        // un slot europeo en el que el jugador juega y todavía no se disputó, este avance es
        // exclusivamente el partido europeo y NO mueve la jornada de liga.
        const euroSchedule = computeEuroSchedule(totalJornadas);
        const euroSlot = euroSchedule.get(nextJornada);
        const jugadorJuegaEuro = !!euroSlot && (
            playerEnEuroSlot(career.champions_league, career.club_actual.tm_id, euroSlot)
            || playerEnEuroSlot(career.europa_league, career.club_actual.tm_id, euroSlot)
            || playerEnEuroSlot(career.libertadores, career.club_actual.tm_id, euroSlot)
            || playerEnEuroSlot(career.sudamericana, career.club_actual.tm_id, euroSlot)
        );

        let partidoEuropeo = null;

        // Acumula stats del jugador y registra el partido europeo (grupo o eliminatoria).
        const registrarPartidoJugador = (info) => {
            career.stats_temporada.partidos += 1;
            career.stats_temporada.goles += info.goles_jugador;
            career.stats_temporada.asistencias += info.asistencias_jugador;
            career.stats_temporada.minutos_totales += info.minutos_jugados;
            // Recordamos en qué copa jugó por última vez, para que el panel siga mostrándola
            // aunque después quede eliminado (caso Libertadores→Sudamericana).
            career.ultima_copa = info.competicion;
            // Una roja en copa lo suspende para el próximo partido de copa.
            if (info.roja) career.suspendido_copa_jornadas = (career.suspendido_copa_jornadas || 0) + 1;
            // Tracking de LOGROS: una derrota en copa marca esa competición como NO-invicta esta
            // temporada; una remontada de 0-3 desbloquea el logro correspondiente.
            if (info.gf < info.gc && info.competicion) career.copa_perdida_temporada[info.competicion] = true;
            if (info.remontada03) career.logros_track.remonto_0_3 = true;
            // La moral se mueve con el resultado y los minutos.
            career.moral = PM.nuevaMoral(career.moral, info.gf, info.gc, info.minutos_jugados);
            partidoEuropeo = info;
            career.historial_partidos.push({
                temporada: career.temporada,
                jornada: nextJornada,
                rival_nombre: info.rival_nombre,
                local: info.local,
                gf: info.gf,
                gc: info.gc,
                minutos_jugados: info.minutos_jugados,
                goles_jugador: info.goles_jugador,
                asistencias_jugador: info.asistencias_jugador,
                amarilla: info.amarilla,
                roja: info.roja,
                competicion: info.competicion,
            });
            if (career.historial_partidos.length > 10) {
                career.historial_partidos = career.historial_partidos.slice(-10);
            }
        };

        // Registra un partido de copa que el jugador se perdió por estar suspendido (roja
        // previa en copa). El club lo juega igual (sin el jugador), pero no suma stats.
        const registrarPartidoCopaSuspendido = (info) => {
            career.ultima_copa = info.competicion;
            partidoEuropeo = { ...info, minutos_jugados: 0, goles_jugador: 0, asistencias_jugador: 0, amarilla: false, roja: false, suspendido: true };
            career.historial_partidos.push({
                temporada: career.temporada,
                jornada: nextJornada,
                rival_nombre: info.rival_nombre,
                local: info.local,
                gf: info.gf,
                gc: info.gc,
                minutos_jugados: 0,
                goles_jugador: 0,
                asistencias_jugador: 0,
                amarilla: false,
                roja: false,
                competicion: info.competicion,
                suspendido: true,
            });
            if (career.historial_partidos.length > 10) {
                career.historial_partidos = career.historial_partidos.slice(-10);
            }
        };

        // Simula TODA la ronda europea del slot actual (las cuatro copas, todos los equipos).
        const procesarEuro = () => {
            // Al llegar a la primera eliminatoria, cerrar la fase de grupos de las copas
            // sudamericanas y sortear AMBAS llaves a la vez (los 3º de Libertadores bajan a
            // los 16avos de Sudamericana). La fase de grupos conmebol termina en la jornada 6,
            // así que un jugador sudamericano nunca pasa por el slot de grupo nº8: el cierre
            // se dispara acá, en el primer slot de eliminatoria.
            if (euroSlot.tipo === 'ko'
                && career.libertadores && (career.libertadores.clubs || []).length
                && career.libertadores.fase === 'grupos') {
                startConmebolKnockouts(career.libertadores, career.sudamericana);
            }

            for (const [competicion, comp] of [
                ['champions', career.champions_league],
                ['europa', career.europa_league],
                ['libertadores', career.libertadores],
                ['sudamericana', career.sudamericana],
            ]) {
                if (!comp || !comp.clubs || comp.clubs.length === 0) continue;
                const clubByTmId = new Map(comp.clubs.map(c => [c.tm_id, c]));
                const esConmebol = competicion === 'libertadores' || competicion === 'sudamericana';

                if (euroSlot.tipo === 'grupo') {
                    // ----- Fase de grupos / liga -----
                    const jornada = comp.calendario.find(j => j.jornada === euroSlot.ronda);
                    if (jornada) {
                        for (const partido of jornada.partidos) {
                            if (partido.jugado || !partido.home_tm_id || !partido.away_tm_id) continue;
                            const homeClub = clubByTmId.get(partido.home_tm_id);
                            const awayClub = clubByTmId.get(partido.away_tm_id);
                            if (!homeClub || !awayClub) continue;

                            const esLocalJug = partido.home_tm_id === career.club_actual.tm_id;
                            const esVisitaJug = partido.away_tm_id === career.club_actual.tm_id;
                            const esSuPartido = esLocalJug || esVisitaJug;
                            const suspendidoCopa = esSuPartido && (career.suspendido_copa_jornadas || 0) > 0;

                            let player = null;
                            if (esSuPartido && !suspendidoCopa) {
                                const rivalClub = esLocalJug ? awayClub : homeClub;
                                player = buildPlayer(career, esLocalJug, rivalClub, `${competicion}${career.temporada}-g${euroSlot.ronda}-${rivalClub.tm_id}`);
                            }

                            const { gh, ga, minutosJugador, golesJugador, asistenciasJugador, amarilla, roja, remontada03 } = simulateMatch(homeClub, awayClub, player);
                            partido.gh = gh;
                            partido.ga = ga;
                            partido.jugado = true;
                            updateTabla(homeClub, awayClub, gh, ga);

                            if (player) {
                                registrarPartidoJugador({
                                    competicion, fase: 'grupos',
                                    local: player.isHome,
                                    rival_nombre: player.isHome ? awayClub.nombre : homeClub.nombre,
                                    gf: player.isHome ? gh : ga,
                                    gc: player.isHome ? ga : gh,
                                    minutos_jugados: minutosJugador, goles_jugador: golesJugador,
                                    asistencias_jugador: asistenciasJugador, amarilla, roja, remontada03,
                                });
                            } else if (suspendidoCopa) {
                                career.suspendido_copa_jornadas -= 1;
                                registrarPartidoCopaSuspendido({
                                    competicion, fase: 'grupos',
                                    local: esLocalJug,
                                    rival_nombre: esLocalJug ? awayClub.nombre : homeClub.nombre,
                                    gf: esLocalJug ? gh : ga,
                                    gc: esLocalJug ? ga : gh,
                                });
                            }
                        }
                    }
                    // Tras la última jornada de grupos, arranca la fase eliminatoria.
                    // Tras la última jornada de grupos arranca la eliminatoria. Las copas
                    // sudamericanas usan su propio sorteo acoplado (startConmebolKnockouts,
                    // disparado arriba en el primer slot de KO), así que acá solo las europeas.
                    if (euroSlot.ronda === EURO_ROUNDS && comp.fase === 'grupos' && !esConmebol) {
                        startKnockout(comp);
                    }
                } else if (euroSlot.tipo === 'ko' && comp.fase === 'knockout' && euroSlot.ronda === comp.ko_ronda) {
                    // ----- Eliminatorias a ida y vuelta (final a partido único). Empate global → penales. -----
                    const ronda = comp.ko_ronda;
                    const pares = pairsFromVivos(comp.ko_vivos);
                    const esVuelta = euroSlot.leg === 'vuelta';
                    const esIda = euroSlot.leg === 'ida';

                    // En la vuelta se invierte la localía respecto a la ida.
                    const idaByKey = new Map((comp.ko_ida || []).map(r => [`${r.home_tm_id}|${r.away_tm_id}`, r]));
                    const winners = [];
                    const idaNueva = [];

                    for (const [aId, bId] of pares) {
                        if (!bId) { winners.push(aId); continue; } // BYE
                        // localía: ida → a de local; vuelta → b de local; final → a de local.
                        const localId = esVuelta ? bId : aId;
                        const visitaId = esVuelta ? aId : bId;
                        const homeClub = clubByTmId.get(localId);
                        const awayClub = clubByTmId.get(visitaId);
                        if (!homeClub || !awayClub) { winners.push(aId); continue; }

                        const esLocalJug = localId === career.club_actual.tm_id;
                        const esVisitaJug = visitaId === career.club_actual.tm_id;
                        const esSuPartido = esLocalJug || esVisitaJug;
                        const suspendidoCopa = esSuPartido && (career.suspendido_copa_jornadas || 0) > 0;

                        let player = null;
                        if (esSuPartido && !suspendidoCopa) {
                            const rivalClub = esLocalJug ? awayClub : homeClub;
                            player = buildPlayer(career, esLocalJug, rivalClub, `${competicion}${career.temporada}-k${ronda}-${euroSlot.leg}-${rivalClub.tm_id}`);
                        }

                        const { gh, ga, minutosJugador, golesJugador, asistenciasJugador, amarilla, roja, remontada03 } = simulateMatch(homeClub, awayClub, player);

                        let ganador = null, penales = false, globalA = null, globalB = null, penalShootout = null;
                        if (esIda) {
                            // Guardar la ida (a local). No se decide nada todavía.
                            idaNueva.push({ home_tm_id: aId, away_tm_id: bId, gh, ga });
                        } else {
                            // Vuelta o final: calcular global y decidir.
                            const ida = idaByKey.get(`${aId}|${bId}`) || { gh: 0, ga: 0 };
                            if (esVuelta) {
                                // gh = goles del local (b), ga = goles de la visita (a)
                                globalA = ida.gh + ga;  // a: local en ida + visita en vuelta
                                globalB = ida.ga + gh;  // b: visita en ida + local en vuelta
                            } else {
                                // final a partido único (a de local)
                                globalA = gh; globalB = ga;
                            }
                            if (globalA > globalB) ganador = aId;
                            else if (globalB > globalA) ganador = bId;
                            else {
                                penales = true;
                                const esA = (clubByTmId.get(aId)?.estrellas) || 1, esB = (clubByTmId.get(bId)?.estrellas) || 1;
                                penalShootout = simulatePenaltyShootout(esA, esB);
                                ganador = penalShootout.ganador === 'a' ? aId : bId;
                            }
                            winners.push(ganador);
                            comp.ko_resultados.push({ ronda, home_tm_id: aId, away_tm_id: bId, global_home: globalA, global_away: globalB, penales, ganador_tm_id: ganador });
                        }

                        if (player) {
                            const info = {
                                competicion, fase: 'knockout', ronda, leg: euroSlot.leg,
                                local: player.isHome,
                                rival_nombre: player.isHome ? awayClub.nombre : homeClub.nombre,
                                gf: player.isHome ? gh : ga,
                                gc: player.isHome ? ga : gh,
                                minutos_jugados: minutosJugador, goles_jugador: golesJugador,
                                asistencias_jugador: asistenciasJugador, amarilla, roja, remontada03,
                            };
                            if (!esIda) {
                                const yoSoyA = aId === career.club_actual.tm_id;
                                const miGlobal = yoSoyA ? globalA : globalB;
                                const rivalGlobal = yoSoyA ? globalB : globalA;
                                info.penales = penales;
                                info.avanzo = ganador === career.club_actual.tm_id;
                                info.global_favor = miGlobal;
                                info.global_contra = rivalGlobal;
                                // Tanda penal por penal, orientada a favor/contra del jugador (para la animación en modo lento).
                                if (penalShootout) {
                                    info.penal_tanda = penalShootout.tanda.map(k => ({
                                        favor: yoSoyA ? k.equipo === 'a' : k.equipo === 'b',
                                        convertido: k.convertido,
                                        muerte_subita: !!k.muerte_subita,
                                    }));
                                    info.penal_favor = yoSoyA ? penalShootout.golesA : penalShootout.golesB;
                                    info.penal_contra = yoSoyA ? penalShootout.golesB : penalShootout.golesA;
                                }
                            }
                            registrarPartidoJugador(info);
                        } else if (suspendidoCopa) {
                            career.suspendido_copa_jornadas -= 1;
                            registrarPartidoCopaSuspendido({
                                competicion, fase: 'knockout', ronda, leg: euroSlot.leg,
                                local: esLocalJug,
                                rival_nombre: esLocalJug ? awayClub.nombre : homeClub.nombre,
                                gf: esLocalJug ? gh : ga,
                                gc: esLocalJug ? ga : gh,
                            });
                        }
                    }

                    if (esIda) {
                        comp.ko_ida = idaNueva; // queda pendiente la vuelta
                    } else {
                        comp.ko_ida = [];
                        advanceKnockout(comp, winners);
                    }
                }
            }

            // Cierre de la fase de grupos sudamericana: en cuanto se jugó la última jornada de
            // grupos (la 6ª; las copas CONMEBOL no llegan al slot de grupo nº8), arrancamos las
            // eliminatorias de AMBAS copas acá. Así el primer cruce KO (16avos de Sudamericana)
            // ya queda en fase 'knockout' antes del próximo slot y se disputa en su turno propio,
            // en vez de jugarse pegado a la jornada de liga.
            if (euroSlot.tipo === 'grupo'
                && career.libertadores && (career.libertadores.clubs || []).length
                && career.libertadores.fase === 'grupos'
                && (career.libertadores.calendario || []).every(j => j.partidos.every(p => p.jugado))) {
                startConmebolKnockouts(career.libertadores, career.sudamericana);
            }
        };

        if (jugadorJuegaEuro && career.euro_turno_jornada !== nextJornada) {
            procesarEuro();
            career.euro_turno_jornada = nextJornada;
            career.mes = computeMes(nextJornada, totalJornadas);
            career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
            career.proximo_turno_es_euro = computeProximoEsEuro(career, totalJornadas);
            return { career, partidoJugador: null, partidoEuropeo, suspendido: false, esTurnoEuro: true, temporadaFinalizada: false };
        }

        career.jornada_actual = nextJornada;
        career.mes = computeMes(nextJornada, totalJornadas);

        const midJornada = Math.ceil(totalJornadas / 2);

        let partidoJugador = null;
        let suspendidoEsteTurno = false;

        for (const league of career.leagues) {
            const jornadaTotal = roundsForCount(league.clubs.length);

            // Cada liga avanza a su propio ritmo: a esta altura de la temporada del jugador
            // (jornada nextJornada de totalJornadas) debe llevar jugada la misma fracción de
            // sus propias jornadas. En la última jornada del jugador se completan todas.
            const target = nextJornada >= totalJornadas
                ? jornadaTotal
                : Math.floor((nextJornada * jornadaTotal) / totalJornadas);

            const clubByTmId = new Map(league.clubs.map(c => [c.tm_id, c]));
            const esLigaDelJugador = league.pais === career.club_actual.pais && league.division === career.club_actual.division;

            for (const jornada of league.calendario) {
                if (jornada.jornada > target) break;

                for (const partido of jornada.partidos) {
                    if (partido.jugado) continue;
                    if (!partido.home_tm_id || !partido.away_tm_id) continue;

                    const homeClub = clubByTmId.get(partido.home_tm_id);
                    const awayClub = clubByTmId.get(partido.away_tm_id);
                    if (!homeClub || !awayClub) continue;

                    let player = null;
                    let jugadorSuspendidoEnEste = false;
                    if (esLigaDelJugador) {
                        const esSuPartido = partido.home_tm_id === career.club_actual.tm_id || partido.away_tm_id === career.club_actual.tm_id;
                        if (esSuPartido && career.suspendido_jornadas > 0) {
                            jugadorSuspendidoEnEste = true;
                            career.suspendido_jornadas -= 1;
                        } else if (partido.home_tm_id === career.club_actual.tm_id) {
                            player = buildPlayer(career, true, awayClub, `L${career.temporada}-${jornada.jornada}-${awayClub.tm_id}`);
                        } else if (partido.away_tm_id === career.club_actual.tm_id) {
                            player = buildPlayer(career, false, homeClub, `L${career.temporada}-${jornada.jornada}-${homeClub.tm_id}`);
                        }
                    }

                    const { gh, ga, minutosJugador, golesJugador, asistenciasJugador, amarilla, roja, planFeedback, remontada03 } = simulateMatch(homeClub, awayClub, player);

                    if (player && remontada03) career.logros_track.remonto_0_3 = true;

                    if (player && roja) {
                        // Una roja suspende al jugador por la próxima jornada de liga.
                        career.suspendido_jornadas += 1;
                    }
                    if (jugadorSuspendidoEnEste) {
                        suspendidoEsteTurno = true;
                        // No actualiza stats pero registramos el partido perdido por sanción.
                        const esLocalEquipo = partido.home_tm_id === career.club_actual.tm_id;
                        const rivalNombre = esLocalEquipo ? awayClub.nombre : homeClub.nombre;
                        career.historial_partidos.push({
                            temporada: career.temporada,
                            jornada: jornada.jornada,
                            rival_nombre: rivalNombre,
                            local: esLocalEquipo,
                            gf: esLocalEquipo ? gh : ga,
                            gc: esLocalEquipo ? ga : gh,
                            minutos_jugados: 0,
                            goles_jugador: 0,
                            asistencias_jugador: 0,
                            amarilla: false,
                            roja: false,
                            suspendido: true,
                        });
                        if (career.historial_partidos.length > 10) {
                            career.historial_partidos = career.historial_partidos.slice(-10);
                        }
                    }

                    partido.gh = gh;
                    partido.ga = ga;
                    partido.jugado = true;

                    updateTabla(homeClub, awayClub, gh, ga);

                    if (player) {
                        career.stats_temporada.partidos += 1;
                        career.stats_temporada.goles += golesJugador;
                        career.stats_temporada.asistencias += asistenciasJugador;
                        career.stats_temporada.minutos_totales += minutosJugador;

                        partidoJugador = {
                            local: player.isHome,
                            rival_nombre: player.isHome ? awayClub.nombre : homeClub.nombre,
                            gf: player.isHome ? gh : ga,
                            gc: player.isHome ? ga : gh,
                            minutos_jugados: minutosJugador,
                            goles_jugador: golesJugador,
                            asistencias_jugador: asistenciasJugador,
                            amarilla,
                            roja,
                            plan_feedback: planFeedback,
                        };
                        // La moral se mueve con el resultado y los minutos del partido de liga.
                        career.moral = PM.nuevaMoral(career.moral, partidoJugador.gf, partidoJugador.gc, minutosJugador);

                        career.historial_partidos.push({
                            temporada: career.temporada,
                            jornada: jornada.jornada,
                            rival_nombre: partidoJugador.rival_nombre,
                            local: partidoJugador.local,
                            gf: partidoJugador.gf,
                            gc: partidoJugador.gc,
                            minutos_jugados: minutosJugador,
                            goles_jugador: golesJugador,
                            asistencias_jugador: asistenciasJugador,
                            amarilla,
                            roja,
                        });
                        if (career.historial_partidos.length > 10) {
                            career.historial_partidos = career.historial_partidos.slice(-10);
                        }
                    }
                }
            }
        }

        if (nextJornada === midJornada) {
            // Ventana de enero: garantizamos al menos una oferta para que el mercado
            // de mitad de temporada siempre se sienta vivo.
            const ofertas = generateTransferOffers({
                leagues: career.leagues,
                clubActual: career.club_actual,
                rating: career.rating,
                edad: career.edad,
                minimo: 1,
            });
            // La posición que muestra la oferta = posición ACTUAL (mitad de temporada) del club,
            // derivada de la MISMA simulación determinística que el backend aplica al fichar. Así
            // la posición de la oferta coincide con la que verás al incorporarte (antes mostraba
            // la tabla en curso/cero y al fichar saltaba a otra posición).
            for (const oferta of ofertas) {
                const lg = career.leagues.find(l => l.clubs.some(c => c.tm_id === oferta.tm_id));
                if (!lg) continue;
                let idx;
                if (esLigaDelJugador(lg, career)) {
                    // Club de TU MISMA liga: usamos la posición REAL de la tabla en curso (la que ves
                    // vos), no la simulación determinística (que es para ligas ajenas que no se juegan).
                    idx = sortStandings(lg.clubs).findIndex(c => c.tm_id === oferta.tm_id);
                } else {
                    const { tabla } = partialLeagueAlIncorporarse(career, lg);
                    idx = tabla.findIndex(c => c.tm_id === oferta.tm_id);
                }
                oferta.posicion_en_liga = idx >= 0 ? idx + 1 : null;
                oferta.posicion_es_final = false;
            }
            career.ofertas_transferencia = ofertas;
        }

        // Si el jugador NO jugaba copa en este slot (eliminado / no clasificado), las
        // competiciones europeas igual avanzan junto con la jornada de liga (no genera
        // confusión porque no hay partido del jugador). Si SÍ jugaba, su partido europeo ya
        // se disputó en su turno propio y este flag impide repetirlo.
        if (euroSlot && career.euro_turno_jornada !== nextJornada) {
            procesarEuro();
        }

        career.valor_mercado = computeMarketValue(career.rating, career.edad, career.club_actual?.estrellas);
        career.proximo_turno_es_euro = computeProximoEsEuro(career, totalJornadas);
        return { career, partidoJugador, partidoEuropeo, suspendido: suspendidoEsteTurno, esTurnoEuro: false, temporadaFinalizada: false };
}

export { runTurn, prunePlayedFixtures, finalizeOtherLeagues, setupSeasonCalendars, simulateLeagueToFinal, esLigaDelJugador, updateTabla, computeMes, playerEnEuroSlot, computeProximoEsEuro, computeSeleccionNext, worldOpts };
